import { collection, query as firestoreQuery, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { SiteAnalytics, DailyAnalytics, PageAnalytics, AnalyticsEvent, AnalyticsSession } from '../types/analytics';
import { LandingPage } from '../types';

export interface AnalyticsQuery {
  siteId: string;
  startDate: Date;
  endDate: Date;
}

export class AnalyticsService {
  /**
   * サイトの分析データを取得（実データ）
   */
  static async getSiteAnalytics(query: AnalyticsQuery, site: LandingPage): Promise<SiteAnalytics> {
    try {
      // Firestoreから実際のデータを取得
      const [events, sessions] = await Promise.all([
        this.getEvents(query),
        this.getSessions(query)
      ]);

      // 常に実データを使用（データがなければ0を表示）
      return this.processAnalyticsData(query, site, events, sessions);
    } catch (error) {
      // エラー時は空のデータを返す
      return this.processAnalyticsData(query, site, [], []);
    }
  }

  /**
   * イベントデータを取得
   */
  private static async getEvents(analyticsQuery: AnalyticsQuery): Promise<AnalyticsEvent[]> {
    const eventsRef = collection(db, 'analytics_events');
    const q = firestoreQuery(
      eventsRef,
      where('siteId', '==', analyticsQuery.siteId),
      where('timestamp', '>=', Timestamp.fromDate(analyticsQuery.startDate)),
      where('timestamp', '<=', Timestamp.fromDate(analyticsQuery.endDate)),
      orderBy('timestamp', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        timestamp: data.timestamp.toDate()
      } as AnalyticsEvent;
    });
  }

  /**
   * セッションデータを取得（public）
   */
  static async getSessions(analyticsQuery: AnalyticsQuery): Promise<AnalyticsSession[]> {
    const sessionsRef = collection(db, 'analytics_sessions');
    const q = firestoreQuery(
      sessionsRef,
      where('siteId', '==', analyticsQuery.siteId),
      where('startTime', '>=', Timestamp.fromDate(analyticsQuery.startDate)),
      where('startTime', '<=', Timestamp.fromDate(analyticsQuery.endDate)),
      orderBy('startTime', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startTime: data.startTime.toDate(),
        endTime: data.endTime?.toDate()
      } as AnalyticsSession;
    });
  }

  /**
   * 実データから分析データを生成
   */
  private static processAnalyticsData(
    query: AnalyticsQuery,
    site: LandingPage,
    _events: AnalyticsEvent[],
    sessions: AnalyticsSession[]
  ): SiteAnalytics {
    const pages = site.pages || [];
    // 静的HTMLではpage.idを使用しているため、page.idを優先的に使用
    // page.idが存在しない場合はpage.pageIdを使用（後方互換性のため）
    const pageIds = pages.map(page => page.id || page.pageId);

    // ページ別データの集計用マップ
    const pageDataMap = new Map<string, {
      pageViews: number; // 到達数（表示回数）
      totalDuration: number; // 合計滞在時間
      visitCount: number; // 訪問回数（平均計算用）
      exitCount: number; // 離脱数
      ctaClicks: number; // CTAクリック数
    }>();

    // ページIDを初期化
    pageIds.forEach(pageId => {
      pageDataMap.set(pageId, {
        pageViews: 0,
        totalDuration: 0,
        visitCount: 0,
        exitCount: 0,
        ctaClicks: 0
      });
    });

    // セッションから集計
    const uniqueUsersByDay = new Map<string, Set<string>>();
    const sessionsReachedPage = new Map<string, Set<string>>();
    let totalCtaClicks = 0;
    const ctaButtonClicksMap = new Map<string, number>();

    sessions.forEach(session => {
      const dateStr = session.startTime.toISOString().split('T')[0];
      
      // ユニークユーザー数（日別）
      if (!uniqueUsersByDay.has(dateStr)) {
        uniqueUsersByDay.set(dateStr, new Set());
      }
      uniqueUsersByDay.get(dateStr)!.add(session.sessionId);

      // CTAクリック数を集計
      totalCtaClicks += session.ctaClicks || 0;
      
      // CTAボタンIDごとのクリック数を集計
      const ctaButtonClicks = session.ctaButtonClicks;
      if (ctaButtonClicks) {
        Object.keys(ctaButtonClicks).forEach(buttonId => {
          const clicks = ctaButtonClicks[buttonId] || 0;
          if (!ctaButtonClicksMap.has(buttonId)) {
            ctaButtonClicksMap.set(buttonId, 0);
          }
          ctaButtonClicksMap.set(buttonId, ctaButtonClicksMap.get(buttonId)! + clicks);
        });
      }

      // 初回訪問ページの到達数をカウント（到達率計算用）
      const firstVisitedPages = session.firstVisitedPages || [];
      firstVisitedPages.forEach(pageId => {
        // ステップ到達率用（セッションごとに1回だけカウント）
        if (!sessionsReachedPage.has(pageId)) {
          sessionsReachedPage.set(pageId, new Set());
        }
        sessionsReachedPage.get(pageId)!.add(session.sessionId);
      });

      // 各ページ訪問を処理（PV数、滞在時間、CTAクリック数を計算）
      if (session.pageVisits && Array.isArray(session.pageVisits)) {
        session.pageVisits.forEach(visit => {
          const pageId = visit.pageId;
          if (!pageDataMap.has(pageId)) {
            pageDataMap.set(pageId, {
              pageViews: 0,
              totalDuration: 0,
              visitCount: 0,
              exitCount: 0,
              ctaClicks: 0
            });
          }

          const pageData = pageDataMap.get(pageId)!;
          // PV数 = 訪問回数
          pageData.pageViews++;
          pageData.visitCount++;
          
          // durationを計算（startTimeとendTimeの差分、秒単位）
          if (visit.startTime && visit.endTime) {
            const startMs = typeof visit.startTime === 'number' ? visit.startTime : visit.startTime.getTime();
            const endMs = typeof visit.endTime === 'number' ? visit.endTime : visit.endTime.getTime();
            const durationInSeconds = (endMs - startMs) / 1000; // 秒単位
            pageData.totalDuration += durationInSeconds;
          }
          
          // CTAクリック数を集計
          pageData.ctaClicks += visit.ctaClicks || 0;
        });
      }



      // 離脱ページをカウント
      if (session.exitPageId && pageDataMap.has(session.exitPageId)) {
        pageDataMap.get(session.exitPageId)!.exitCount++;
      }
    });

    // ページデータの完成
    const pageData: PageAnalytics[] = pageIds.map(pageId => {
      const data = pageDataMap.get(pageId) || {
        pageViews: 0,
        totalDuration: 0,
        visitCount: 0,
        exitCount: 0,
        ctaClicks: 0
      };

      // 対応するページ情報を取得（page.idまたはpage.pageIdで検索）
      const pageInfo = pages.find(p => (p.id || p.pageId) === pageId);

      const sessionsReached = sessionsReachedPage.get(pageId)?.size || 0; // そのページに到達したセッション数
      const exitCount = data.exitCount;
      const totalSessions = sessions.length;
      // そのページでの平均滞在時間を計算
      const pageVisits = sessions.flatMap(session => 
        session.pageVisits?.filter(visit => visit.pageId === pageId) || []
      );
      const avgDuration = pageVisits.length > 0 
        ? pageVisits.reduce((sum, visit) => {
            if (visit.startTime && visit.endTime) {
              const startMs = typeof visit.startTime === 'number' ? visit.startTime : visit.startTime.getTime();
              const endMs = typeof visit.endTime === 'number' ? visit.endTime : visit.endTime.getTime();
              const durationInSeconds = (endMs - startMs) / 1000; // 秒単位
              return sum + durationInSeconds;
            }
            return sum;
          }, 0) / pageVisits.length
        : 0;
      
      return {
        pageId,
        pageTitle: pageId.replace(/^0+/, '') || pageId,
        pageViews: data.pageViews, // PV数 = そのページの訪問回数
        uniqueUsers: sessionsReached, // 到達数 = そのページに到達したセッション数
        stepReachRate: totalSessions > 0 ? (sessionsReached / totalSessions) * 100 : 0,
        exitCount,
        exitRate: sessionsReached > 0 ? (exitCount / sessionsReached) * 100 : 0,
        averageSessionDuration: avgDuration,
        ctaClicks: data.ctaClicks, // ページ別CTAクリック数
        backgroundImage: pageInfo?.backgroundImage // 背景画像
      };
    });

    // 日別データ（簡易実装）
    const dailyData: DailyAnalytics[] = [];

    // 総計
    const totalSiteViews = sessions.length; // 表示回数 = セッション数
    const totalUniqueUsers = new Set(sessions.map(s => s.sessionId)).size;
    // セッション全体の平均滞在時間を計算（秒単位）
    const averageSessionDuration = sessions.length > 0
      ? sessions.reduce((sum, s) => {
          if (s.startTime && s.endTime) {
            const startMs = s.startTime.getTime();
            const endMs = s.endTime.getTime();
            const durationInSeconds = (endMs - startMs) / 1000; // 秒単位
            return sum + durationInSeconds;
          }
          return sum;
        }, 0) / sessions.length
      : 0;

    return {
      siteId: query.siteId,
      period: {
        startDate: query.startDate,
        endDate: query.endDate
      },
      metrics: {
        pageViews: totalSiteViews, // サイト全体の表示回数（セッション数）
        uniqueUsers: totalUniqueUsers,
        stepReachRates: pageData.reduce((acc, page) => {
          acc[page.pageId] = page.stepReachRate;
          return acc;
        }, {} as { [pageId: string]: number }),
        exitCounts: pageData.reduce((acc, page) => {
          acc[page.pageId] = page.exitCount;
          return acc;
        }, {} as { [pageId: string]: number }),
        exitRates: pageData.reduce((acc, page) => {
          acc[page.pageId] = page.exitRate;
          return acc;
        }, {} as { [pageId: string]: number }),
        averageSessionDuration,
        ctaClicks: totalCtaClicks || 0,
        ctaButtonClicks: Object.fromEntries(ctaButtonClicksMap)
      },
      dailyData,
      pageData
    };
  }
}
